'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, Fragment, useCallback, useRef, useState, useEffect } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import * as CustomPropTypes from '../../../constants/PropTypes';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import H4 from 'UIComponents/elements/headings/H4';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import { SEARCH_RESULTS_MAX } from 'ecosystem-marketplace-core/constants/search';
import { DEFAULT_SEARCH_LENGTH, NAV_BAR_HEIGHT_INT } from '../../../constants';
import * as FilterKey from '../../../constants/search/FilterKey';
import SearchResult from '../../../records/SearchResult';
import { isFulfilled, isSucceeded, isFailed } from 'ecosystem-marketplace-core/utils/requestState';
import { getFiltersHeadingI18nKey } from '../../../utils/i18n';
import ResultsSection from 'ecosystem-marketplace-core/components/storefront/ResultsSection';
import StorefrontResultsHeader from 'ecosystem-marketplace-core/components/storefront/StorefrontResultsHeader';
import ResultsMaxWarning from 'ecosystem-marketplace-core/components/storefront/ResultsMaxWarning';
import ResultsCount from './ResultsCount';
import ResultsFilters from './ResultsFilters';
import ResultsGrid from './ResultsGrid';
import ResultsHeaderTitle from './ResultsHeaderTitle';
import SortDropdown from './SortDropdown';
import SupplementalResultsSection from './SupplementalResultsSection';
import ScrollToResults from 'ecosystem-marketplace-core/components/storefront/ScrollToResults';
import MediaQuery from 'ecosystem-marketplace-core/components/layout/MediaQuery';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import MobileFilterPanelNavigator from './mobile/MobileFilterPanelNavigator';

const getI18nKey = key => `appMarketplace.storefront.results.resultsPage.${key}`;

const renderSupplementalResultsSection = ({
  index,
  onResultClick,
  supplementalSearchResult,
  supplementalResultProps,
  trackInteraction
}) => /*#__PURE__*/_jsx(SupplementalResultsSection, {
  onResultClick: (result, resultIndex) => onResultClick(result, resultIndex, supplementalSearchResult.items.size),
  resultProps: supplementalResultProps,
  searchResult: supplementalSearchResult,
  trackInteraction: trackInteraction
}, `supplemental-results-${index}`);

const ResultsPage = ({
  activePage,
  failedComponent: FailedComponent,
  filterable,
  filters,
  offset,
  onUpdateFilters,
  onClearFilters,
  onFilterChange,
  onPageChange,
  onResultClick,
  onSortChange,
  requestState,
  resultProps,
  resultsPerPage,
  searchResult,
  sortable,
  supplementalResults,
  trackInteraction
}) => {
  const {
    [FilterKey.SUPER_CATEGORY]: superCategory,
    [FilterKey.CATEGORY]: category,
    [FilterKey.SEARCH]: searchValue
  } = filters;
  const resultsRef = useRef(null);
  const {
    items: searchResults,
    total: searchResultsTotal
  } = searchResult;
  const searchResultsLength = searchResults.size;
  const hasResults = !!searchResultsTotal; // TODO cleanup/refactor

  const [allCategoriesResult = new SearchResult(), restSupplementalResults] = useMemo(() => {
    if (category || superCategory) {
      return [supplementalResults.first(), supplementalResults.rest()];
    }

    return [undefined, supplementalResults];
  }, [category, superCategory, supplementalResults]);
  const supplementalResultProps = useMemo(() => {
    const rest = _objectWithoutPropertiesLoose(resultProps, ["superCategory"]);

    return rest;
  }, [resultProps]);
  const {
    filters: allCategoriesFilters,
    items: allCategoriesResults,
    total: allCategoriesTotal
  } = allCategoriesResult;
  const allCategoriesResultsLength = allCategoriesResults.size;
  const hasAllCategoriesResults = !!allCategoriesTotal;
  const showAllCategoriesSearchResults = !!(searchValue && !hasResults && hasAllCategoriesResults);
  const handlePageChange = useCallback(({
    target: {
      value
    }
  }) => {
    onPageChange(value, showAllCategoriesSearchResults ? allCategoriesFilters : filters);
  }, [allCategoriesFilters, filters, onPageChange, showAllCategoriesSearchResults]);
  const [resultsLength, resultsTotal, resultsPageCount] = useMemo(() => {
    let length;
    let total;

    if (showAllCategoriesSearchResults) {
      length = allCategoriesResultsLength;
      total = allCategoriesTotal;
    } else {
      length = searchResultsLength;
      total = searchResultsTotal;
    }

    total = Math.min(total, SEARCH_RESULTS_MAX);
    return [length, total, Math.ceil(total / resultsPerPage)];
  }, [allCategoriesResultsLength, allCategoriesTotal, resultsPerPage, searchResultsLength, searchResultsTotal, showAllCategoriesSearchResults]);
  const filtersHeadingI18nKey = useMemo(() => getFiltersHeadingI18nKey(filters), [filters]);

  const renderedResultsHeaderTitle = /*#__PURE__*/_jsx(ResultsHeaderTitle, {
    category: category,
    searchValue: searchValue,
    superCategory: superCategory
  });

  const renderedResultsCount = isFulfilled(requestState) ? /*#__PURE__*/_jsx(ResultsCount, {
    filtersHeadingI18nKey: filtersHeadingI18nKey,
    hasResults: hasResults,
    offset: offset,
    resultsLength: resultsLength,
    resultsTotal: resultsTotal,
    searchValue: searchValue,
    showAllCategoriesSearchResults: showAllCategoriesSearchResults
  }) : null;
  const validFilterTagKeys = filters.getValidFilterTagKeys();
  const renderedDefaultFilterBar = !validFilterTagKeys.isEmpty() ? /*#__PURE__*/_jsx(ResultsFilters, {
    filters: filters,
    onClearFilters: onClearFilters,
    onFilterChange: onFilterChange,
    trackInteraction: trackInteraction,
    validFilterTagKeys: validFilterTagKeys
  }) : null;
  const renderedResultsFilters = filterable ? /*#__PURE__*/_jsx(MediaQuery, {
    maxScreenSize: ScreenSizes.SM,
    alternate: renderedDefaultFilterBar,
    children: /*#__PURE__*/_jsx(ResultsFilters, {
      filters: filters,
      isMobile: true,
      onClearFilters: onClearFilters,
      onFilterChange: onFilterChange,
      onSortChange: onSortChange,
      trackInteraction: trackInteraction,
      validFilterTagKeys: validFilterTagKeys,
      startSlot: /*#__PURE__*/_jsx(MobileFilterPanelNavigator, {
        filters: filters,
        onUpdateFilters: onUpdateFilters,
        searchTerm: searchValue,
        trackInteraction: trackInteraction
      })
    })
  }) : null;
  const computedSortable = useMemo(() => {
    if (!sortable || !(hasResults || showAllCategoriesSearchResults)) {
      return false;
    }

    return true;
  }, [hasResults, showAllCategoriesSearchResults, sortable]);
  const renderedSortDropdown = computedSortable ? /*#__PURE__*/_jsx(SortDropdown, {
    filters: filters,
    onSortChange: (field, order) => {
      onSortChange(field, order);
      trackInteraction('change sort', {
        sortField: field,
        sortOrder: order
      }, 'storefront-change-sort');
    },
    onFilterChange: onFilterChange
  }) : null;
  const renderedSupplementalResults = useMemo(() => {
    if (!isSucceeded(requestState) || !searchValue || restSupplementalResults.isEmpty()) {
      return null;
    }

    return restSupplementalResults.valueSeq().map((supplementalSearchResult, index) => renderSupplementalResultsSection({
      index,
      onResultClick,
      supplementalSearchResult,
      supplementalResultProps,
      trackInteraction
    })).toArray();
  }, [onResultClick, requestState, restSupplementalResults, searchValue, supplementalResultProps, trackInteraction]);
  let renderedPageContent;

  if (isFailed(requestState) && FailedComponent) {
    renderedPageContent = /*#__PURE__*/_jsx(FailedComponent, {});
  } else if (isSucceeded(requestState) && !(hasResults || showAllCategoriesSearchResults)) {
    renderedPageContent = /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsxs(ResultsSection, {
        gutterSize: "xl",
        children: [/*#__PURE__*/_jsx(H4, {
          children: searchValue ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: getI18nKey('noResultsEmptyState.searchTitle_jsx'),
            elements: {
              b: 'span'
            },
            options: {
              searchValue,
              pageHeading: unescapedText(filtersHeadingI18nKey)
            }
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: getI18nKey('noResultsEmptyState.filterTitle'),
            options: {
              pageHeading: unescapedText(filtersHeadingI18nKey)
            }
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getI18nKey('noResultsEmptyState.message')
          })
        })]
      }), renderedSupplementalResults]
    });
  } else {
    const renderedResultsGrid = /*#__PURE__*/_jsx(ResultsGrid, {
      className: "vr-hidden",
      onResultClick: (result, index) => onResultClick(result, index, showAllCategoriesSearchResults ? allCategoriesResultsLength : searchResultsLength),
      requestState: requestState,
      resultProps: showAllCategoriesSearchResults ? supplementalResultProps : resultProps,
      results: showAllCategoriesSearchResults ? allCategoriesResults : searchResults
    });

    const showMaxWarning = activePage >= resultsPageCount && resultsTotal >= SEARCH_RESULTS_MAX;
    const renderedResultsPagination = isSucceeded(requestState) && resultsTotal > resultsLength ? /*#__PURE__*/_jsx(UIPaginator, {
      onPageChange: handlePageChange,
      page: activePage,
      pageCount: resultsPageCount
    }) : null;
    renderedPageContent = /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(ResultsSection, {
        ref: resultsRef,
        children: renderedResultsGrid
      }), showMaxWarning && /*#__PURE__*/_jsx(ResultsSection, {
        className: "flex-row justify-center",
        children: /*#__PURE__*/_jsx(ResultsMaxWarning, {})
      }), /*#__PURE__*/_jsx(ResultsSection, {
        children: renderedResultsPagination
      })]
    });
  }

  const [scrollToResultsOpacity, setScrollToResultsOpacity] = useState(0);
  const [scrollToResultsFixed, setScrollToResultsFixed] = useState(true);
  const resultsEl = findDOMNode(resultsRef.current);
  const renderedScrollToResults = resultsEl && searchResultsLength ? /*#__PURE__*/_jsx(ScrollToResults, {
    containerWidth: resultsEl.offsetWidth,
    opacity: scrollToResultsOpacity,
    fixed: scrollToResultsFixed
  }) : null;
  const detectResultsVisible = useCallback(() => {
    const rect = resultsEl && resultsEl.getBoundingClientRect();

    if (rect) {
      setScrollToResultsFixed(resultsEl.parentElement.getBoundingClientRect().bottom > window.innerHeight);
      setScrollToResultsOpacity((NAV_BAR_HEIGHT_INT - rect.bottom) / 200);
    }
  }, [resultsEl]);
  useEffect(() => {
    window.addEventListener('scroll', detectResultsVisible);
    return () => {
      window.removeEventListener('scroll', detectResultsVisible);
    };
  }, [detectResultsVisible]);
  useEffect(() => {
    // Update scroll to results visibility on re-render
    detectResultsVisible();
  });
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(MediaQuery, {
      maxScreenSize: ScreenSizes.SM,
      alternate: /*#__PURE__*/_jsx(StorefrontResultsHeader, {
        title: renderedResultsHeaderTitle,
        renderedResultsFilters: renderedResultsFilters,
        renderedSortDropdown: renderedSortDropdown,
        resultsCount: renderedResultsCount,
        searchTerm: searchValue,
        ungatedMobile: true
      }),
      children: /*#__PURE__*/_jsx(StorefrontResultsHeader, {
        title: renderedResultsHeaderTitle,
        renderedResultsFilters: renderedResultsFilters,
        resultsCount: renderedResultsCount,
        searchTerm: searchValue,
        ungatedMobile: true
      })
    }), renderedPageContent, renderedScrollToResults]
  });
};

ResultsPage.propTypes = {
  activePage: PropTypes.number.isRequired,
  failedComponent: PropTypes.func,
  filterable: PropTypes.bool,
  filters: CustomPropTypes.storefrontFilters.isRequired,
  offset: PropTypes.number,
  onClearFilters: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onResultClick: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  requestState: CustomPropTypes.requestState.isRequired,
  resultProps: PropTypes.object,
  resultsPerPage: PropTypes.number,
  searchResult: PropTypes.instanceOf(SearchResult).isRequired,
  sortable: PropTypes.bool,
  supplementalResults: ImmutablePropTypes.orderedSetOf(PropTypes.instanceOf(SearchResult)).isRequired,
  trackInteraction: PropTypes.func.isRequired,
  onUpdateFilters: PropTypes.func.isRequired
};
ResultsPage.defaultProps = {
  offset: 0,
  resultProps: {},
  resultsPerPage: DEFAULT_SEARCH_LENGTH,
  sortable: true
};
export default ResultsPage;